import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const TenantContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detect tenant from subdomain or default to "cao"
const detectTenant = () => {
  const hostname = window.location.hostname;
  // dev.cubiq.com.ar -> "cao" (default for dev)
  // cao.cubiq.com.ar -> "cao"
  // sae.cubiq.com.ar -> "sae"
  const parts = hostname.split(".");
  if (parts[0] === "dev" || parts[0] === "localhost") {
    return localStorage.getItem("cubiq_tenant") || "cao";
  }
  return parts[0] || "cao";
};

export const TenantProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(detectTenant());

  const loadTenant = async (slug) => {
    setTenantLoading(true);
    try {
      const response = await axios.get(API_URL + "/api/tenant/config/" + slug);
      setTenantConfig(response.data);
      setTenantError(null);

      // Apply CSS variables
      const c = response.data.config;
      document.documentElement.style.setProperty("--color-primary", c.color_primario);
      document.documentElement.style.setProperty("--color-secondary", c.color_secundario);
      document.documentElement.style.setProperty("--color-accent", c.color_acento);
      document.documentElement.style.setProperty("--color-cta", c.color_cta);
    } catch (error) {
      console.error("Error loading tenant config:", error);
      setTenantError("Tenant no encontrado");
    } finally {
      setTenantLoading(false);
    }
  };

  useEffect(() => {
    loadTenant(tenantSlug);
  }, [tenantSlug]);

  const switchTenant = (newSlug) => {
    localStorage.setItem("cubiq_tenant", newSlug);
    setTenantSlug(newSlug);
  };

  return (
    <TenantContext.Provider value={{
      tenant: tenantConfig,
      tenantSlug,
      tenantLoading,
      tenantError,
      switchTenant,
      reloadTenant: () => loadTenant(tenantSlug),
      // Shorthand accessors
      config: tenantConfig?.config,
      landing: tenantConfig?.landing,
      servicios: tenantConfig?.servicios || [],
      beneficios: tenantConfig?.beneficios || [],
      contacto: tenantConfig?.contacto,
      footer: tenantConfig?.footer,
      modulos: tenantConfig?.modulos || [],
      isModuleActive: (mod) => tenantConfig?.modulos?.some(m => m.modulo === mod && m.activo) || false,
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
